export type WidgetTheme = Record<string, string>;

type WidgetTokenDefinition = {
  light: string;
  dark: string;
  source?: string;
  darkSource?: string;
  kind?: "color" | "raw";
};

const color = (
  source: string,
  light: string,
  dark: string,
  darkSource = source,
): WidgetTokenDefinition => ({
  source,
  darkSource,
  light,
  dark,
  kind: "color",
});

const fixed = (value: string): WidgetTokenDefinition => ({
  light: value,
  dark: value,
});

const WIDGET_TOKEN_DEFINITIONS: Record<string, WidgetTokenDefinition> = {
  "--surface-0": color("--ds-surface-default", "#ffffff", "#30302e"),
  "--surface-1": color("--ds-surface-subtle", "#f7f5f4", "#262624"),
  "--surface-2": color("--ds-surface-inset", "#ece9e6", "#141413"),
  "--surface-3": color("--ds-surface-raised", "#ffffff", "#30302e"),
  "--surface-panel": color("--ds-surface-subtle", "#f7f5f4", "#262624"),
  "--surface-popover": color("--ds-surface-raised", "#ffffff", "#30302e"),
  "--bg-accent": color("--ds-accent-faint", "#eff5fe", "#0d1f33"),
  "--bg-danger": color("--ds-danger-subtle", "#ffe4e1", "#3f071a"),
  "--bg-success": color("--ds-success-subtle", "#e5f7e7", "#0c2a18"),
  "--bg-warning": color("--ds-warning-subtle", "#ffefe3", "#351f00"),
  "--bg-pro": { light: "#f3e8ff", dark: "#0d1f33", kind: "color" },
  "--bg-neutral": color("--ds-surface-inset", "#ece9e6", "#141413"),
  "--text-primary": color("--ds-text-primary", "#2c2c2c", "#faf9f5"),
  "--text-secondary": color("--ds-text-secondary", "#606060", "#c2c0b6"),
  "--text-muted": color("--ds-text-muted", "#655d58", "#8e8c85"),
  "--text-disabled": color("--ds-text-faint", "#a19a94", "#8e8c85"),
  "--text-accent": color(
    "--ds-blue-700",
    "#084399",
    "#8bb3f3",
    "--ds-accent-text",
  ),
  "--text-danger": color("--ds-danger-text", "#921921", "#f0a4a1"),
  "--text-success": color("--ds-success-text", "#066a2c", "#80c291"),
  "--text-warning": color("--ds-warning-text", "#874804", "#d99a38"),
  "--text-pro": { light: "#6b21a8", dark: "#8bb3f3", kind: "color" },
  "--border": color(
    "--ds-border-subtle",
    "#dad5d1",
    "rgba(222, 220, 209, 0.15)",
  ),
  "--border-strong": color(
    "--ds-border-strong",
    "#c1bbb6",
    "rgba(222, 220, 209, 0.3)",
  ),
  "--border-stronger": color(
    "--ds-cream-400",
    "#a19a94",
    "rgba(222, 220, 209, 0.4)",
    "--ds-border-strong",
  ),
  "--border-accent": color(
    "--ds-blue-300",
    "#6ea9ff",
    "rgba(106, 154, 234, 0.5)",
    "--ds-focus-ring",
  ),
  "--border-danger": color("--ds-red-200", "#fdb1ab", "#923939", "--ds-danger"),
  "--border-success": color(
    "--ds-green-200",
    "#ade0b5",
    "#3b7f49",
    "--ds-success",
  ),
  "--border-warning": color(
    "--ds-orange-200",
    "#f9c9a6",
    "#a66f1a",
    "--ds-warning",
  ),
  "--border-pro": {
    light: "#d8b4fe",
    dark: "rgba(106, 154, 234, 0.5)",
    kind: "color",
  },
  "--border-neutral": color(
    "--ds-cream-300",
    "#c1bbb6",
    "rgba(222, 220, 209, 0.3)",
    "--ds-border-strong",
  ),
  "--fill-accent": color("--ds-accent-action", "#0d6fe1", "#faf9f5"),
  "--fill-accent-hover": color(
    "--ds-accent-action-hover",
    "#0a59cc",
    "#c2c0b6",
  ),
  "--fill-danger": color("--ds-destructive-action", "#c21f2c", "#923939"),
  "--fill-danger-hover": color(
    "--ds-red-700",
    "#921921",
    "#f0a4a1",
    "--ds-danger-text",
  ),
  "--fill-success": color(
    "--ds-green-600",
    "#048c3c",
    "#3b7f49",
    "--ds-success",
  ),
  "--fill-success-hover": color(
    "--ds-green-700",
    "#066a2c",
    "#80c291",
    "--ds-success-text",
  ),
  "--fill-warning": color(
    "--ds-orange-600",
    "#b15f03",
    "#a66f1a",
    "--ds-warning",
  ),
  "--fill-warning-hover": color(
    "--ds-orange-700",
    "#874804",
    "#d99a38",
    "--ds-warning-text",
  ),
  "--fill-pro": { light: "#7e22ce", dark: "#faf9f5", kind: "color" },
  "--fill-pro-hover": {
    light: "#6b21a8",
    dark: "#c2c0b6",
    kind: "color",
  },
  "--fill-neutral": color(
    "--ds-gray-600",
    "#606060",
    "#c2c0b6",
    "--ds-text-secondary",
  ),
  "--fill-neutral-hover": color(
    "--ds-gray-700",
    "#424242",
    "#faf9f5",
    "--ds-text-primary",
  ),
  "--on-accent": color(
    "--ds-white",
    "#ffffff",
    "#141413",
    "--ds-accent-action-text",
  ),
  "--on-danger": color("--ds-white", "#ffffff", "#ffffff"),
  "--on-success": color(
    "--ds-white",
    "#ffffff",
    "#faf9f5",
    "--ds-text-primary",
  ),
  "--on-warning": color(
    "--ds-cream-900",
    "#161412",
    "#faf9f5",
    "--ds-text-primary",
  ),
  "--on-pro": { light: "#ffffff", dark: "#141413", kind: "color" },
  "--on-neutral": color("--ds-white", "#ffffff", "#ffffff"),
  "--font-sans": {
    source: "--ds-font-sans",
    light:
      '"Anthropic Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    dark: '"Anthropic Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    kind: "raw",
  },
  "--font-voice": {
    source: "--ds-font-serif",
    light: '"Libre Baskerville", Georgia, "Times New Roman", serif',
    dark: '"Libre Baskerville", Georgia, "Times New Roman", serif',
    kind: "raw",
  },
  "--font-mono": fixed(
    'ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace',
  ),
  "--radius": fixed("8px"),
  "--pad-sm": fixed("8px"),
  "--pad-md": fixed("12px"),
  "--pad-lg": fixed("16px"),
  "--pad-xl": fixed("24px"),
  "--gap-xs": fixed("4px"),
  "--gap-sm": fixed("8px"),
  "--gap-md": fixed("12px"),
  "--gap-lg": fixed("16px"),
  "--gap-xl": fixed("24px"),
  "--h-control": fixed("36px"),
  "--shadow-sm": {
    source: "--ds-shadow-sm",
    light: "0 1px 2px rgba(22, 20, 18, 0.06)",
    dark: "0 1px 2px rgba(0, 0, 0, 0.18)",
    kind: "raw",
  },
  "--shadow-md": {
    source: "--ds-shadow-md",
    light: "0 4px 12px rgba(22, 20, 18, 0.08)",
    dark: "0 4px 12px rgba(0, 0, 0, 0.24)",
    kind: "raw",
  },
  "--shadow-lg": {
    source: "--ds-shadow-lg",
    light: "0 12px 32px rgba(22, 20, 18, 0.12)",
    dark: "0 12px 32px rgba(0, 0, 0, 0.3)",
    kind: "raw",
  },
  "--shadow-popover": {
    source: "--ds-shadow-overlay",
    light: "0 17px 45px rgba(0, 0, 0, 0.16)",
    dark: "0 17px 45px rgba(0, 0, 0, 0.4)",
    kind: "raw",
  },
  "--dur-fast": fixed("120ms"),
  "--dur-snap": fixed("80ms"),
  "--dur-base": fixed("180ms"),
  "--dur-slow": fixed("300ms"),
  "--ease-out": fixed("cubic-bezier(0.16, 1, 0.3, 1)"),
  "--ease-snap": fixed("cubic-bezier(0.2, 0, 0, 1)"),
  "--ease-overshoot": fixed("cubic-bezier(0.34, 1.56, 0.64, 1)"),
};

export function resolveWidgetTheme(): WidgetTheme {
  const dark = document.documentElement.dataset.theme === "dark";
  if (!document.body) {
    return Object.fromEntries(
      Object.entries(WIDGET_TOKEN_DEFINITIONS).map(([name, definition]) => [
        name,
        dark ? definition.dark : definition.light,
      ]),
    );
  }

  const probe = document.createElement("span");
  probe.hidden = true;
  document.body.append(probe);
  const rootStyle = getComputedStyle(document.documentElement);
  const theme = Object.fromEntries(
    Object.entries(WIDGET_TOKEN_DEFINITIONS).map(([name, definition]) => {
      const fallback = dark ? definition.dark : definition.light;
      const source = dark
        ? (definition.darkSource ?? definition.source)
        : definition.source;
      if (!source) return [name, fallback];
      if (definition.kind === "color") {
        probe.style.color = `var(${source}, ${fallback})`;
        const resolved = getComputedStyle(probe).color.trim();
        return [
          name,
          resolved && !resolved.includes("var(") ? resolved : fallback,
        ];
      }
      const resolved = rootStyle.getPropertyValue(source).trim();
      return [
        name,
        resolved && !resolved.includes("var(") ? resolved : fallback,
      ];
    }),
  );
  probe.remove();
  return theme;
}

export function themeDeclarations(theme: WidgetTheme): string {
  return Object.entries(theme)
    .map(([name, value]) => `${name}: ${value};`)
    .join("\n");
}

export function widgetHostStyles(): string {
  return `
    *, *::before, *::after { box-sizing: border-box; }
    html, body { margin: 0; background: transparent; }
    body {
      width: 100%;
      max-width: 680px;
      margin: 0 auto;
      color: var(--text-primary);
      background: transparent;
      font: 400 16px/1.5 var(--font-sans);
    }
    h1, h2, h3, p { margin-top: 0; }
    h1, h2, h3 { color: var(--text-primary); font-weight: 500; line-height: 1.25; }
    b, strong { font-weight: 500; }
    h1 { font-size: 22px; }
    h2 { font-size: 18px; }
    h3 { font-size: 16px; }
    small, figcaption { color: var(--text-muted); font-size: 11px; }
    a { color: var(--text-accent); }
    button, input, select, textarea { color: inherit; font: inherit; }
    button,
    input:not([type="checkbox"]):not([type="radio"]):not([type="range"]),
    select {
      min-height: var(--h-control);
      padding: 0 var(--pad-md);
      border: 0.5px solid var(--border);
      border-radius: var(--radius);
      background: var(--surface-1);
    }
    textarea {
      min-height: calc(var(--h-control) * 2);
      padding: var(--pad-sm) var(--pad-md);
      border: 0.5px solid var(--border);
      border-radius: var(--radius);
      background: var(--surface-1);
      resize: vertical;
    }
    button { cursor: pointer; transition: background var(--dur-fast) var(--ease-out), border-color var(--dur-fast) var(--ease-out); }
    button:hover { background: var(--surface-2); border-color: var(--border-strong); }
    input::placeholder, textarea::placeholder { color: var(--text-muted); }
    input[type="range"] {
      width: 100%;
      height: 18px;
      margin: 0;
      appearance: none;
      background: transparent;
    }
    input[type="range"]::-webkit-slider-runnable-track { height: 4px; border-radius: var(--radius); background: var(--border-strong); }
    input[type="range"]::-webkit-slider-thumb { width: 18px; height: 18px; margin-top: -7px; appearance: none; border: 0; border-radius: 50%; background: var(--fill-accent); }
    :focus-visible { outline: 0; box-shadow: 0 0 0 2px var(--border-accent); }
    svg, canvas, img { display: block; max-width: 100%; }
  `;
}

type ColorRamp = readonly [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];

const LIGHT_RAMPS: Record<string, ColorRamp> = {
  purple: [
    "#f3e8ff",
    "#e9d5ff",
    "#d8b4fe",
    "#a855f7",
    "#9333ea",
    "#7e22ce",
    "#581c87",
  ],
  teal: [
    "#ccfbf1",
    "#99f6e4",
    "#5eead4",
    "#14b8a6",
    "#0d9488",
    "#0f766e",
    "#134e4a",
  ],
  coral: [
    "#ffe4e1",
    "#fdb1ab",
    "#fb8d87",
    "#f56361",
    "#ea2f3b",
    "#c21f2c",
    "#921921",
  ],
  blue: [
    "#eff5fe",
    "#cfe2ff",
    "#9ec5ff",
    "#3d8cff",
    "#0d6fe1",
    "#0a59cc",
    "#084399",
  ],
  green: [
    "#e5f7e7",
    "#d2edd5",
    "#ade0b5",
    "#4ebf6a",
    "#04ac4a",
    "#048c3c",
    "#066a2c",
  ],
  amber: [
    "#ffefe3",
    "#fae1cf",
    "#f9c9a6",
    "#ea9146",
    "#db7807",
    "#b15f03",
    "#874804",
  ],
  pink: [
    "#fce7f3",
    "#fbcfe8",
    "#f9a8d4",
    "#ec4899",
    "#db2777",
    "#be185d",
    "#831843",
  ],
  indigo: [
    "#eef2ff",
    "#e0e7ff",
    "#c7d2fe",
    "#818cf8",
    "#6366f1",
    "#4f46e5",
    "#3730a3",
  ],
  gray: [
    "#f8f8f8",
    "#ebebeb",
    "#d7d7d7",
    "#9b9b9b",
    "#838383",
    "#606060",
    "#424242",
  ],
};

const DARK_RAMPS: Record<string, ColorRamp> = {
  purple: [
    "#3b0764",
    "#581c87",
    "#6b21a8",
    "#a855f7",
    "#c084fc",
    "#d8b4fe",
    "#f3e8ff",
  ],
  teal: [
    "#042f2e",
    "#134e4a",
    "#0f766e",
    "#14b8a6",
    "#2dd4bf",
    "#5eead4",
    "#ccfbf1",
  ],
  coral: [
    "#330b0b",
    "#611215",
    "#921921",
    "#f56361",
    "#fb8d87",
    "#fdb1ab",
    "#ffe4e1",
  ],
  blue: [
    "#041e46",
    "#0d2c58",
    "#084399",
    "#0d6fe1",
    "#3d8cff",
    "#9ec5ff",
    "#eff5fe",
  ],
  green: [
    "#072810",
    "#01481b",
    "#066a2c",
    "#04ac4a",
    "#4ebf6a",
    "#ade0b5",
    "#e5f7e7",
  ],
  amber: [
    "#341c07",
    "#5a2d00",
    "#874804",
    "#db7807",
    "#ea9146",
    "#f9c9a6",
    "#ffefe3",
  ],
  pink: [
    "#500724",
    "#831843",
    "#9d174d",
    "#db2777",
    "#ec4899",
    "#f9a8d4",
    "#fce7f3",
  ],
  indigo: [
    "#1e1b4b",
    "#312e81",
    "#3730a3",
    "#6366f1",
    "#818cf8",
    "#c7d2fe",
    "#eef2ff",
  ],
  gray: [
    "#171717",
    "#2c2c2c",
    "#424242",
    "#838383",
    "#9b9b9b",
    "#d7d7d7",
    "#f8f8f8",
  ],
};

function linearChannel(channel: number): number {
  const normalized = channel / 255;
  if (normalized <= 0.04045) return normalized / 12.92;
  return ((normalized + 0.055) / 1.055) ** 2.4;
}

function rampTextColor(hex: string): "#000000" | "#ffffff" {
  const red = linearChannel(Number.parseInt(hex.slice(1, 3), 16));
  const green = linearChannel(Number.parseInt(hex.slice(3, 5), 16));
  const blue = linearChannel(Number.parseInt(hex.slice(5, 7), 16));
  const luminance = red * 0.2126 + green * 0.7152 + blue * 0.0722;
  const blackContrast = (luminance + 0.05) / 0.05;
  const whiteContrast = 1.05 / (luminance + 0.05);
  return blackContrast > whiteContrast ? "#000000" : "#ffffff";
}

export function svgRampStyles(dark: boolean): string {
  const ramps = dark ? DARK_RAMPS : LIGHT_RAMPS;
  const rules: string[] = [];
  for (const [name, stops] of Object.entries(ramps)) {
    const defaultText = rampTextColor(stops[3]);
    rules.push(
      `.c-${name}{fill:${stops[3]};stroke:${stops[3]};color:${stops[3]};--svg-on-color:${defaultText};--svg-on-secondary:${defaultText}}`,
    );
    stops.forEach((stop, index) => {
      const number = index + 1;
      const stopText = rampTextColor(stop);
      rules.push(
        `.c-${name}-${number},.c-${name}.s${number},.c-${name} .s${number}{fill:${stop};stroke:${stop};color:${stop};--svg-on-color:${stopText};--svg-on-secondary:${stopText}}`,
      );
    });
  }
  rules.push(
    ".t{fill:var(--svg-on-color,var(--text-primary));stroke:none;color:var(--svg-on-color,var(--text-primary));font:400 16px/1.5 var(--font-sans)}",
    ".ts{fill:var(--svg-on-secondary,var(--text-secondary));stroke:none;color:var(--svg-on-secondary,var(--text-secondary));font:400 13px/1.5 var(--font-sans)}",
    ".th{fill:var(--svg-on-color,var(--text-primary));stroke:none;color:var(--svg-on-color,var(--text-primary));font:500 16px/1.25 var(--font-sans)}",
  );
  return rules.join("\n");
}
